import { useState, useEffect, useRef } from 'react';
import { PRERENDER } from '../lib/prerender';

const rawReviews = [
  { name: 'Melinda Normand', text: "I found this place close to home — I took my 4-year-old son Oakley in.", speed: 1.1 },
  { name: 'S J', text: "I take my toddler to Raj and he is so patient with him, putting cartoons on the tv.", speed: 0.95 },
  { name: 'Elijah Crevier', text: "Sahil was a proper gentleman! Place was a vibe. Best place in Abbotsford.", speed: 1.35 },
  { name: 'fateh brar', text: "Pav is next level. His fades are always clean, crisp, and detailed.", speed: 1.25 },
  { name: 'Ander Herrera', text: "Excellent service, excellent communication. They understand 100% what you want.", speed: 0.9 },
  { name: 'Shivam Rana', text: "Haircut with Rony was an amazing experience! Attention to detail was impeccable.", speed: 1.15 },
  { name: 'Travis Hawkins', text: "These guys are the best. Top notch and they know what they are doing.", speed: 0.8 },
  { name: 'Kenn Cooper', text: "RONI was the example of a professional barber. Sharp eye for detail.", speed: 1.45 },
  { name: 'Sarah Pahl', text: "Raj transformed my son's hair. Fades are amazing, teenager approved.", speed: 1.1 },
];

// Double the reviews for mobile "wall" effect
const allReviews = [...rawReviews, ...rawReviews.map(r => ({...r, speed: r.speed * 1.1}))];

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
);

const Stars = () => (
  <div style={{ display: 'flex', gap: '2px', marginBottom: '8px' }}>
    {[...Array(5)].map((_, i) => (
      <svg key={i} width="12" height="12" viewBox="0 0 24 24" fill="#C6A24A"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
    ))}
  </div>
);

const ReviewsSection = () => {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(PRERENDER);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = vw < 768;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setHasStarted(true); }, { threshold: 0.1 });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;
    let start = null;
    const animate = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / 1000, 1); // 1s snap
      setCount(Math.floor(prog * 400));
      if (prog < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasStarted]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const val = Math.max(0, -rect.top) / (containerRef.current.offsetHeight - window.innerHeight);
      setProgress(Math.min(val, 1));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getPos = (i) => {
    if (isMobile) {
      // Mobile "Coverage" - 3 columns, tightly packed
      const cols = [-100, 0, 100];
      const x = cols[i % 3];
      const y = 600 + (i * 140); // Dense vertical stack
      return { x, y, rot: (i % 2 === 0 ? -5 : 5) };
    }
    const columns = [-450, -180, 180, 450]; 
    const x = columns[i % 4];
    const y = 800 + (Math.floor(i / 1.5) * 160);
    return { x, y, rot: (i % 3 === 0 ? -6 : 6) };
  };

  return (
    <div ref={containerRef} style={{ height: '220vh', background: '#000', position: 'relative' }}>
      {/* BACKGROUND IMAGE LAYER */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url("https://brocode.b-cdn.net/brocode/98AED732-B703-43F8-89D7-4F6F52DC6D02.jpg")',
        backgroundSize: 'cover', backgroundPosition: 'center',
        opacity: 0.15, // Keep it subtle so text is readable
        filter: 'grayscale(100%)',
        zIndex: 0
      }} />

      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        
        {/* CENTER UNIT - Together in one flexbox */}
        <div style={{ 
          display: 'flex', flexDirection: 'column', alignItems: 'center', 
          textAlign: 'center', zIndex: 100, pointerEvents: 'none',
          gap: isMobile ? '20px' : '40px'
        }}>
          <div>
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 900, fontSize: isMobile ? '8rem' : '15rem', color: '#fff', margin: 0, lineHeight: 0.8 }}>
              {count}<span style={{ color: '#C6A24A' }}>+</span>
            </h2>
                 <br />
            <p style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.4em', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: isMobile ? '0.7rem' : '1.1rem' }}>
              5-Star Google Reviews
            </p>
          </div>

          <div style={{ pointerEvents: 'auto' }}>
            <a 
              href="https://www.google.com/maps/place/Bro+Code+Barbershop/@49.0345779,-122.3518334,15z/data=!4m8!3m7!1s0x5485cb9fe99ab6bf:0x730ccb9889170ca1!8m2!3d49.0345643!4d-122.3415551!9m1!1b1!16s%2Fg%2F11ld28s1w9?entry=ttu&g_ep=EgoyMDI2MDMxMS4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <button
                style={{
                  padding: isMobile ? '12px 24px' : '18px 50px',
                  background: 'rgba(0,0,0,0.5)',
                  border: '2px solid #C6A24A',
                  color: '#C6A24A',
                  fontFamily: 'Oswald',
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  fontWeight: 700,
                  fontSize: isMobile ? '0.7rem' : '1rem',
                  cursor: 'pointer',
                  transition: '0.3s',
                  backdropFilter: 'blur(5px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#C6A24A'
                  e.target.style.color = '#000'
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(0,0,0,0.5)'
                  e.target.style.color = '#C6A24A'
                }}
              >
                See the rest of our reviews
              </button>
            </a>
          </div>
        </div>

        {/* REVIEWS STAR FIELD */}
        <div style={{ position: 'absolute', inset: 0 }}>
          {allReviews.map((rev, i) => {
            const pos = getPos(i);
            const travel = progress * 3800 * rev.speed;
            
            return (
              <div key={i} style={{
                position: 'absolute',
                left: `calc(50% + ${pos.x}px)`,
                top: `${pos.y - travel}px`,
                width: isMobile ? '160px' : '340px',
                padding: isMobile ? '14px' : '28px',
                background: 'rgba(10, 10, 10, 0.98)',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(198,162,74,0.25)',
                transform: `translateX(-50%) rotate(${pos.rot + (progress * 15)}deg)`,
                opacity: progress > 0.01 ? 1 : 0,
                boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
                zIndex: 10
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                   <Stars />
                   <GoogleLogo />
                </div>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? '0.65rem' : '0.95rem', lineHeight: 1.4, margin: '0 0 10px 0' }}>
                  "{rev.text}"
                </p>
                <div style={{ color: '#C6A24A', fontSize: isMobile ? '0.55rem' : '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>
                  {rev.name}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default ReviewsSection;
